/**
 * Achieve section — horizontal carousel with accordion-style expand-to-wide cards.
 * Desktop: inline expand on + click. Mobile: full-screen modal with arrow navigation.
 */
(function () {
  'use strict';

  var MOBILE_MAX_WIDTH_MQ = '(max-width: 989px)';

  function prefersReducedMotion() {
    try {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  function isMobileViewport() {
    try {
      return window.matchMedia && window.matchMedia(MOBILE_MAX_WIDTH_MQ).matches;
    } catch (e) {
      return false;
    }
  }

  function AchieveSection(root) {
    this.root = root;
    this.track = root.querySelector('[data-achieve-track]');
    this.cards = root.querySelectorAll('[data-achieve-card]');
    this.prevBtn = root.querySelector('[data-achieve-prev]');
    this.nextBtn = root.querySelector('[data-achieve-next]');
    this.mobileModal = root.querySelector('[data-achieve-mobile-modal]');
    this.mobileTrack = root.querySelector('[data-achieve-mobile-track]');
    this.mobilePanels = root.querySelectorAll('[data-achieve-mobile-panel]');
    this.mobileCloseButtons = root.querySelectorAll('[data-achieve-mobile-close]');
    this.mobilePrevBtn = root.querySelector('[data-achieve-mobile-prev]');
    this.mobileNextBtn = root.querySelector('[data-achieve-mobile-next]');

    this.activeIndex = 0;
    this.mobileOpenIndex = -1;
    this._syncingModal = false;
    this._syncingMain = false;

    this._onScroll = this.updateArrows.bind(this);
    this._onKeydown = this._handleKeydown.bind(this);
    this._onResize = this._handleResize.bind(this);
    this._busyTimer = null;

    // The modal is position:fixed, but the section (overflow:hidden, and
    // sometimes transformed by reveal animations) clips or re-anchors it, so
    // taps appeared to do nothing. Mount it on <body> instead.
    if (this.mobileModal && this.mobileModal.parentNode !== document.body) {
      document.body.appendChild(this.mobileModal);
    }

    this._onModalScroll = this._handleModalScroll.bind(this);
    this._modalScrollTimer = null;

    this.bind();
    this.updateArrows();
  }

  AchieveSection.prototype.bind = function () {
    var self = this;

    if (this.track) {
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      this.track.addEventListener('keydown', this._onKeydown);
    }

    if (this.mobileTrack) {
      this.mobileTrack.addEventListener('scroll', this._onModalScroll, { passive: true });
    }

    if (this.mobilePrevBtn) {
      this.mobilePrevBtn.addEventListener('click', function () {
        self.navigateMobilePanel(-1);
      });
    }

    if (this.mobileNextBtn) {
      this.mobileNextBtn.addEventListener('click', function () {
        self.navigateMobilePanel(1);
      });
    }

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        self.scrollByCard(-1);
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        self.scrollByCard(1);
      });
    }

    if (this.cards && this.cards.length) {
      this.cards.forEach(function (card) {
        var toggle = card.querySelector('[data-achieve-toggle]');
        if (!toggle) return;

        toggle.addEventListener('click', function (event) {
          event.preventDefault();
          self.toggleCard(card);
        });
      });
    }

    if (this.mobileCloseButtons && this.mobileCloseButtons.length) {
      this.mobileCloseButtons.forEach(function (button) {
        button.addEventListener('click', function () {
          self.closeMobileModal();
        });
      });
    }

    window.addEventListener('resize', this._onResize);
  };

  AchieveSection.prototype.destroy = function () {
    if (this.track) {
      this.track.removeEventListener('scroll', this._onScroll);
      this.track.removeEventListener('keydown', this._onKeydown);
    }
    window.removeEventListener('resize', this._onResize);
    if (this._busyTimer) window.clearTimeout(this._busyTimer);
    if (this._modalScrollTimer) window.clearTimeout(this._modalScrollTimer);
    this.closeMobileModal();
    if (this.mobileModal && this.mobileModal.parentNode) {
      this.mobileModal.parentNode.removeChild(this.mobileModal);
    }
  };

  // Finger swipe inside the modal: keep the open index and arrows in sync.
  AchieveSection.prototype._handleModalScroll = function () {
    if (this.mobileOpenIndex < 0 || this._syncingModal || !this.mobileTrack) return;
    var self = this;
    if (this._modalScrollTimer) window.clearTimeout(this._modalScrollTimer);
    this._modalScrollTimer = window.setTimeout(function () {
      var width = self.mobileTrack.clientWidth;
      if (!width) return;
      var index = self.clampIndex(Math.round(self.mobileTrack.scrollLeft / width));
      if (index === self.mobileOpenIndex) return;
      self.mobileOpenIndex = index;
      self.activeIndex = index;
      self.updateMobileArrows();
    }, 80);
  };

  AchieveSection.prototype._handleResize = function () {
    if (this.mobileOpenIndex >= 0 && !isMobileViewport()) {
      this.closeMobileModal();
    }
    if (!isMobileViewport()) return;
    this.collapseAll();
  };

  AchieveSection.prototype._handleKeydown = function (event) {
    if (!event) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.scrollByCard(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.scrollByCard(1);
    } else if (event.key === 'Escape') {
      if (this.mobileOpenIndex >= 0 && isMobileViewport()) {
        this.closeMobileModal();
      } else {
        this.collapseAll();
      }
    }
  };

  AchieveSection.prototype.getGap = function () {
    if (!this.track) return 14;
    try {
      return parseInt(window.getComputedStyle(this.track).gap, 10) || 14;
    } catch (e) {
      return 14;
    }
  };

  AchieveSection.prototype.getScrollAmount = function () {
    if (!this.track) return 0;
    var card = this.track.querySelector('[data-achieve-card]');
    if (!card) return 0;
    return (card.offsetWidth || 0) + this.getGap();
  };

  AchieveSection.prototype.getCardIndex = function (card) {
    if (!card) return -1;
    var index = card.getAttribute('data-card-index');
    if (index !== null && index !== '') return parseInt(index, 10);
    if (!this.cards) return -1;
    for (var i = 0; i < this.cards.length; i++) {
      if (this.cards[i] === card) return i;
    }
    return -1;
  };

  AchieveSection.prototype.clampIndex = function (index) {
    if (!this.cards || !this.cards.length) return 0;
    return Math.max(0, Math.min(this.cards.length - 1, index));
  };

  AchieveSection.prototype.hasExpandedCard = function () {
    if (isMobileViewport()) return false;
    if (!this.cards || !this.cards.length) return false;
    for (var i = 0; i < this.cards.length; i++) {
      if (this.cards[i].classList.contains('is-expanded')) return true;
    }
    return false;
  };

  AchieveSection.prototype.scrollByCard = function (direction) {
    if (!this.track || this.hasExpandedCard()) return;
    if (this.mobileOpenIndex >= 0 && isMobileViewport()) return;

    var amount = this.getScrollAmount();
    if (!amount) return;

    var behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    if (typeof this.track.scrollBy === 'function') {
      this.track.scrollBy({ left: direction * amount, behavior: behavior });
    } else {
      this.track.scrollLeft += direction * amount;
    }
  };

  AchieveSection.prototype.updateMobileArrows = function () {
    var maxIndex = this.mobilePanels && this.mobilePanels.length
      ? this.mobilePanels.length - 1
      : 0;
    var isOpen = this.mobileOpenIndex >= 0;

    if (this.mobilePrevBtn) {
      this.mobilePrevBtn.disabled = !isOpen || this.mobileOpenIndex <= 0;
    }
    if (this.mobileNextBtn) {
      this.mobileNextBtn.disabled = !isOpen || this.mobileOpenIndex >= maxIndex;
    }
  };

  AchieveSection.prototype.navigateMobilePanel = function (direction) {
    if (this.mobileOpenIndex < 0 || !direction) return;

    var newIndex = this.clampIndex(this.mobileOpenIndex + direction);
    if (newIndex === this.mobileOpenIndex) return;

    this.mobileOpenIndex = newIndex;
    this.activeIndex = newIndex;

    var behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    this._scrollMobileToIndex(newIndex, behavior);
    this._syncMainToIndex(newIndex, behavior);
    this.updateMobileArrows();
  };

  AchieveSection.prototype.updateArrows = function () {
    if (!this.track) return;

    if (this.hasExpandedCard() || (this.mobileOpenIndex >= 0 && isMobileViewport())) {
      if (this.prevBtn) this.prevBtn.disabled = true;
      if (this.nextBtn) this.nextBtn.disabled = true;
      return;
    }

    var maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;

    if (this.prevBtn) {
      this.prevBtn.disabled = this.track.scrollLeft <= 2;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.track.scrollLeft >= maxScroll;
    }
  };

  AchieveSection.prototype.collapseAll = function () {
    var self = this;
    if (!this.cards || !this.cards.length) return;

    this.cards.forEach(function (card) {
      self.setExpanded(card, false);
    });
  };

  AchieveSection.prototype.toggleCard = function (card) {
    if (!card) return;

    if (isMobileViewport()) {
      this.openMobileModal(this.getCardIndex(card));
      return;
    }

    var isExpanded = card.classList.contains('is-expanded');

    if (isExpanded) {
      this.setExpanded(card, false);
      return;
    }

    this.collapseAll();
    this.setExpanded(card, true);
    this._scrollCardIntoView(card);
  };

  AchieveSection.prototype.setExpanded = function (card, expanded) {
    if (!card) return;

    card.classList.toggle('is-expanded', !!expanded);

    var toggle = card.querySelector('[data-achieve-toggle]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      toggle.setAttribute('aria-label', expanded ? 'Collapse card' : 'Expand card');
    }

    var expandedPane = card.querySelector('.achieve__expanded');
    if (expandedPane) {
      expandedPane.setAttribute('aria-hidden', expanded ? 'false' : 'true');
      expandedPane.inert = !expanded;
    }

    if (this.track) {
      var self = this;
      this.track.classList.add('is-busy');
      if (this._busyTimer) window.clearTimeout(this._busyTimer);
      this._busyTimer = window.setTimeout(function () {
        if (self.track) self.track.classList.remove('is-busy');
      }, prefersReducedMotion() ? 0 : 560);
    }

    this.updateArrows();
  };

  AchieveSection.prototype._isLastCard = function (card) {
    if (!card || !this.cards || !this.cards.length) return false;
    return card === this.cards[this.cards.length - 1];
  };

  AchieveSection.prototype._scrollCardIntoView = function (card, behavior) {
    if (!card || !this.track) return;

    var scrollBehavior = behavior || (prefersReducedMotion() ? 'auto' : 'smooth');
    var inline = this._isLastCard(card) ? 'center' : 'start';

    try {
      card.scrollIntoView({ behavior: scrollBehavior, inline: inline, block: 'nearest' });
    } catch (e) {
      var left = card.offsetLeft;
      if (this._isLastCard(card) && this.track.clientWidth) {
        left = Math.max(0, left - (this.track.clientWidth - card.offsetWidth) / 2);
      }
      this.track.scrollTo
        ? this.track.scrollTo({ left: left, behavior: scrollBehavior })
        : (this.track.scrollLeft = left);
    }
  };

  AchieveSection.prototype._scrollMobileToIndex = function (index, behavior) {
    if (!this.mobileTrack || !this.mobilePanels || !this.mobilePanels.length) return;

    var panel = this.mobilePanels[this.clampIndex(index)];
    if (!panel) return;

    var scrollBehavior = behavior || (prefersReducedMotion() ? 'auto' : 'smooth');
    this._syncingModal = true;

    try {
      panel.scrollIntoView({ behavior: scrollBehavior, inline: 'start', block: 'nearest' });
    } catch (e) {
      this.mobileTrack.scrollTo
        ? this.mobileTrack.scrollTo({ left: panel.offsetLeft, behavior: scrollBehavior })
        : (this.mobileTrack.scrollLeft = panel.offsetLeft);
    }

    var self = this;
    window.setTimeout(function () {
      self._syncingModal = false;
    }, scrollBehavior === 'smooth' ? 420 : 0);
  };

  AchieveSection.prototype._syncMainToIndex = function (index, behavior) {
    if (!this.cards || !this.cards.length) return;

    var targetIndex = this.clampIndex(index);
    var card = this.cards[targetIndex];
    if (!card) return;

    this.activeIndex = targetIndex;
    this._syncingMain = true;
    this._scrollCardIntoView(card, behavior);
    this.updateArrows();

    var self = this;
    window.setTimeout(function () {
      self._syncingMain = false;
    }, behavior === 'smooth' ? 420 : 0);
  };

  AchieveSection.prototype.openMobileModal = function (index) {
    if (!this.mobileModal || !this.mobileTrack || !this.mobilePanels.length) return;
    if (!isMobileViewport()) return;

    var targetIndex = this.clampIndex(index);
    this.mobileOpenIndex = targetIndex;
    this.activeIndex = targetIndex;

    this.mobileModal.hidden = false;
    this.mobileModal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('achieve-modal-open');

    this._scrollMobileToIndex(targetIndex, 'auto');
    this._syncMainToIndex(targetIndex, prefersReducedMotion() ? 'auto' : 'smooth');
    this.updateArrows();
    this.updateMobileArrows();

    if (this.mobileTrack.focus) {
      try {
        this.mobileTrack.focus({ preventScroll: true });
      } catch (e) {
        this.mobileTrack.focus();
      }
    }
  };

  AchieveSection.prototype.closeMobileModal = function () {
    if (!this.mobileModal || this.mobileOpenIndex < 0) return;

    var closingIndex = this.mobileOpenIndex;

    this.mobileModal.hidden = true;
    this.mobileModal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('achieve-modal-open');
    this.mobileOpenIndex = -1;

    this._syncMainToIndex(closingIndex, prefersReducedMotion() ? 'auto' : 'smooth');
    this.updateArrows();
    this.updateMobileArrows();
  };

  function init(root) {
    if (!root || root.nodeType !== 1) return null;
    if (root.getAttribute('data-achieve-init') === 'true') return root._achieveSection || null;

    root.setAttribute('data-achieve-init', 'true');
    var instance = new AchieveSection(root);
    root._achieveSection = instance;
    return instance;
  }

  function destroy(root) {
    if (!root || root.nodeType !== 1) return;
    var instance = root._achieveSection;
    if (instance && typeof instance.destroy === 'function') instance.destroy();
    delete root._achieveSection;
    root.removeAttribute('data-achieve-init');
  }

  function initAll(scope) {
    var container = scope && scope.nodeType === 1 ? scope : document;
    if (!container.querySelectorAll) return;
    var roots = container.querySelectorAll('[data-achieve-section]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  function destroyAll(scope) {
    var container = scope && scope.nodeType === 1 ? scope : document;
    if (!container.querySelectorAll) return;
    var roots = container.querySelectorAll('[data-achieve-section]');
    for (var i = 0; i < roots.length; i++) destroy(roots[i]);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroyAll(event.target);
  });

  document.addEventListener('shopify:section:reorder', function () {
    initAll(document);
  });
})();
